import React, { useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../../app/hooks';
import { setAskAicaSelectedSources, setAskAicaRightPanelMode, SuggestedQuestion } from '../../../../../../store/dashboard/askAica/actions';
import { DEFAULT_SUGGESTED_QUESTIONS } from '../../../../../../store/dashboard/askAica/constants';
import styles from './AskAicaV2.module.scss';
import DataSourceSelector from './components/DataSourceSelector/DataSourceSelector';
import { chatbotGif } from '../../../../../../assets/hostedassets';
import LeftPanel from './components/LeftPanel/LeftPanel';
import RightPanel from './components/RightPanel/RightPanel';
import MagicUploadIcon from '../../../../../../assets/Svg/MagicuploadIcon';
import { Mixpanel } from '../../../../../../utils/mixpanel';
import { AICA_ASK_AICA_MODAL_OPENED, AICA_VIEW_COMMENT } from '../../../../../../utils/constants/mixpanelEvents/investorEvents';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAskAica } from '../../../../../../store/dashboard/askAica';
import { globalAskAicaService } from '../../../../../../store/dashboard/askAica/globalAskAicaService';
import { useAskAicaFileActions } from './hooks/useAskAicaFileActions';
import DocxDrawer from '../../../../../../common/DocumentViewer/DocxDrawer';
import FileViewerDrawer from '../../../../../../common/DocumentViewer/FileViewerDrawer';
import { SheetEditorDrawer } from '../../../../../../common/SheetEditor';

interface AskAicaV2Props {
    selectedCompany?: any;
    overviewData?: any;
    global?: boolean;
}

const AskAicaV2: React.FC<AskAicaV2Props> = ({ selectedCompany, overviewData, global = false }) => {
    const userData = useAppSelector((state) => state.user.userData);
    const params = useParams();
    const [searchParams] = useSearchParams();
    const dispatch = useAppDispatch();
    const { selectedSources, rightPanelMode } = useAppSelector((state) => state.dashboard.askAicaReducer.uiState);
    const { clearChatMemory, sendMessage, fetchChatHistory, fetchLibraryFiles } = useAskAica();
    const { openFile, addToReports, addingToReports, addedToReports, docxViewer, sheetViewer, fileViewer } = useAskAicaFileActions();

    // Show AskAica on all tabs unless explicitly disabled
    const dashboardConfig = useAppSelector((state) => state.dashboard.dashboardReducer.config);
    const currentTabConfig = dashboardConfig?.selectedTabId ? dashboardConfig.tabConfigs?.[dashboardConfig.selectedTabId] : null;
    const isAskAicaEnabledForTab = currentTabConfig?.showAskAica !== false;

    // Check if any drawer/modal is open (only affects global icon visibility)
    const investorState = useAppSelector((state) => state.investor);
    const commentsThreadState = useAppSelector((state) => state.commentsThreadReducer);
    const aicaGradeDrawerOpen = useAppSelector((state) => state.dashboard.dashboardReducer.aicaGradeData.showAicaGradeDrawer);

    const isAnyDrawerOpen =
        searchParams.get('magicUpload') === 'true' ||
        searchParams.get('needInfo') === 'true' ||
        investorState.dealDrawer.open ||
        investorState.dealApplicationDrawer.open ||
        investorState.showManageTeamDrawer ||
        investorState.customFilterDrawer.open ||
        commentsThreadState.showCommentsThread ||
        aicaGradeDrawerOpen;

    // Ask bar shows on all main pages, global icon hides when drawers are open
    const showAskAica = isAskAicaEnabledForTab;
    const showGlobalIcon = isAskAicaEnabledForTab && !isAnyDrawerOpen;
    const [isExpanded, setIsExpanded] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [showModalContent, setShowModalContent] = useState(false);
    const [showCollapsedContent, setShowCollapsedContent] = useState(true);
    const barRef = useRef<HTMLDivElement>(null);
    const globalIconRef = useRef<HTMLDivElement>(null);
    const timersRef = useRef<number[]>([]);
    const prevBodyOverflowRef = useRef<string | null>(null);
    const [autoSendMessage, setAutoSendMessage] = useState<string | null>(null);
    const autoFlowKeyRef = useRef<string | undefined>(undefined);
    const [initialRect, setInitialRect] = useState<DOMRect | null>(null);
    const [openedFromGlobal, setOpenedFromGlobal] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const orgId = params.id ? params.id : userData?.investorOrgId;
    const sendMessageRef = useRef<((message: string, sources?: string[], flowKey?: string) => void) | null>(null);

    const suggestedQuestions = DEFAULT_SUGGESTED_QUESTIONS;

    const handleOpenModal = (initialMessageOrEvent?: string | React.MouseEvent, flowKey?: string) => {
        if (userData?.userId && orgId) {
            fetchChatHistory();
            fetchLibraryFiles(orgId);
        }

        if (typeof initialMessageOrEvent === 'string') {
            setAutoSendMessage(initialMessageOrEvent);
            autoFlowKeyRef.current = flowKey;
        }

        // track mixpanel event
        Mixpanel.track(AICA_ASK_AICA_MODAL_OPENED, {
            pnoId: userData?.investorOrgId,
            userId: userData?.userId,
            organizationId: orgId,
        });

        // Capture initial dimensions and position with more precision

        if (barRef.current) {
            // Animation from floating bar
            setOpenedFromGlobal(false);
            const rect = barRef.current.getBoundingClientRect();
            setInitialRect(rect);

            // Set CSS variables with exact pixel values (not percentages)
            document.documentElement.style.setProperty('--ask-bar-initial-width', `${rect.width}px`);
            document.documentElement.style.setProperty('--ask-bar-initial-height', `${rect.height}px`);
            document.documentElement.style.setProperty('--ask-bar-initial-top', `${rect.top}px`);
            document.documentElement.style.setProperty('--ask-bar-initial-left', `${rect.left}px`);

            // Also store the transform origin for accurate scaling
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;
            document.documentElement.style.setProperty('--ask-bar-center-x', `${centerX}px`);
            document.documentElement.style.setProperty('--ask-bar-center-y', `${centerY}px`);
        } else if (global && globalIconRef.current) {
            // Animation from global icon when barRef is not available
            setOpenedFromGlobal(true);
            const globalRect = globalIconRef.current.getBoundingClientRect();
            setInitialRect(globalRect);

            // Set CSS variables based on global icon position
            document.documentElement.style.setProperty('--ask-bar-initial-width', `${globalRect.width}px`);
            document.documentElement.style.setProperty('--ask-bar-initial-height', `${globalRect.height}px`);
            document.documentElement.style.setProperty('--ask-bar-initial-top', `${globalRect.top}px`);
            document.documentElement.style.setProperty('--ask-bar-initial-left', `${globalRect.left}px`);

            // Set transform origin to center of global icon
            const centerX = globalRect.left + globalRect.width / 2;
            const centerY = globalRect.top + globalRect.height / 2;
            document.documentElement.style.setProperty('--ask-bar-center-x', `${centerX}px`);
            document.documentElement.style.setProperty('--ask-bar-center-y', `${centerY}px`);
        } else {
            // Fallback for when neither ref is available (global case)
            setOpenedFromGlobal(true);
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            // Use global icon's expected position (bottom: 50px, right: 50px)
            const iconSize = 60; // From CSS
            const bottom = 50;
            const right = 50;

            const globalIconTop = viewportHeight - bottom - iconSize;
            const globalIconLeft = viewportWidth - right - iconSize;

            document.documentElement.style.setProperty('--ask-bar-initial-width', `${iconSize}px`);
            document.documentElement.style.setProperty('--ask-bar-initial-height', `${iconSize}px`);
            document.documentElement.style.setProperty('--ask-bar-initial-top', `${globalIconTop}px`);
            document.documentElement.style.setProperty('--ask-bar-initial-left', `${globalIconLeft}px`);
            document.documentElement.style.setProperty('--ask-bar-center-x', `${globalIconLeft + iconSize / 2}px`);
            document.documentElement.style.setProperty('--ask-bar-center-y', `${globalIconTop + iconSize / 2}px`);
        }

        // Start animation sequence
        setIsAnimating(true);
        setIsClosing(false); // Reset closing state

        // Immediately switch to fixed positioning with initial dimensions
        requestAnimationFrame(() => {
            setIsExpanded(true);

            // Hide collapsed content and show modal content after a delay
            const t1 = window.setTimeout(() => {
                setShowCollapsedContent(false);
                setShowModalContent(true);
            }, 150); // Reduced from 250ms to 150ms
            timersRef.current.push(t1);

            const t2 = window.setTimeout(() => {
                setIsAnimating(false);
            }, 300); // Reduced from 500ms to 300ms
            timersRef.current.push(t2);
        });
    };

    const handleCloseModal = () => {
        // Close any open viewer drawer along with the modal — otherwise it floats over
        // the page after the chat closes, and its blob URL (fileViewer) never gets revoked.
        docxViewer?.onClose();
        sheetViewer?.onClose();
        fileViewer?.onClose();

        setIsAnimating(true);
        setIsClosing(true);
        setShowModalContent(false); // Hide modal content immediately
        setAutoSendMessage(null);

        // If opened from global icon, capture current global icon position for accurate close animation
        if (openedFromGlobal && globalIconRef.current) {
            const globalRect = globalIconRef.current.getBoundingClientRect();

            // Update CSS variables with current global icon position
            document.documentElement.style.setProperty('--ask-bar-initial-width', `${globalRect.width}px`);
            document.documentElement.style.setProperty('--ask-bar-initial-height', `${globalRect.height}px`);
            document.documentElement.style.setProperty('--ask-bar-initial-top', `${globalRect.top}px`);
            document.documentElement.style.setProperty('--ask-bar-initial-left', `${globalRect.left}px`);

            const centerX = globalRect.left + globalRect.width / 2;
            const centerY = globalRect.top + globalRect.height / 2;
            document.documentElement.style.setProperty('--ask-bar-center-x', `${centerX}px`);
            document.documentElement.style.setProperty('--ask-bar-center-y', `${centerY}px`);
        }

        setShowCollapsedContent(true); // Show collapsed content immediately

        requestAnimationFrame(() => {
            setIsExpanded(false);

            // Clear the CSS variables after animation completes
            const t3 = window.setTimeout(() => {
                setIsAnimating(false);
                setIsClosing(false);
                setInitialRect(null);
                setOpenedFromGlobal(false);
                // Clean up CSS variables
                document.documentElement.style.removeProperty('--ask-bar-initial-width');
                document.documentElement.style.removeProperty('--ask-bar-initial-height');
                document.documentElement.style.removeProperty('--ask-bar-initial-top');
                document.documentElement.style.removeProperty('--ask-bar-initial-left');
                document.documentElement.style.removeProperty('--ask-bar-center-x');
                document.documentElement.style.removeProperty('--ask-bar-center-y');
            }, 300); // Reduced from 500ms to 300ms to match opening animation
            timersRef.current.push(t3);
        });
    };

    // Close modal on Escape key
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isExpanded) {
                handleCloseModal();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isExpanded]);

    // Prevent body scroll when modal is fully expanded
    useEffect(() => {
        if (isExpanded) {
            if (prevBodyOverflowRef.current === null) prevBodyOverflowRef.current = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
        } else {
            if (prevBodyOverflowRef.current !== null) {
                document.body.style.overflow = prevBodyOverflowRef.current;
                prevBodyOverflowRef.current = null;
            }
        }
    }, [isExpanded]);

    // Auto-send message when modal content is shown and there's a message to send
    useEffect(() => {
        if (showModalContent && autoSendMessage && sendMessageRef.current) {
            const flowKeyValue = autoFlowKeyRef.current;
            const timer = setTimeout(() => {
                sendMessageRef.current!(autoSendMessage, undefined, flowKeyValue);
                setAutoSendMessage(null);
                autoFlowKeyRef.current = undefined;
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [showModalContent, autoSendMessage]);

    useEffect(() => {
        if (global) {
            globalAskAicaService.registerAskAicaInstance(handleOpenModal, (message: string, sources?: string[], flowKey?: string) => {
                if (sendMessageRef.current) {
                    sendMessageRef.current(message, sources, flowKey);
                } else {
                    sendMessage(message, flowKey);
                }
            });
        }
    }, [global, sendMessage]);

    // Global cleanup on unmount
    useEffect(() => {
        return () => {
            timersRef.current.forEach((id) => clearTimeout(id));
            timersRef.current = [];
            if (prevBodyOverflowRef.current !== null) {
                document.body.style.overflow = prevBodyOverflowRef.current;
                prevBodyOverflowRef.current = null;
            }
            if (global) {
                globalAskAicaService.registerAskAicaInstance(null, null);
            }
            [
                '--ask-bar-initial-width',
                '--ask-bar-initial-height',
                '--ask-bar-initial-top',
                '--ask-bar-initial-left',
                '--ask-bar-center-x',
                '--ask-bar-center-y',
            ].forEach((v) => document.documentElement.style.removeProperty(v));
        };
    }, []);

    const handleDataSourceChange = (sources: string[]) => {
        dispatch(setAskAicaSelectedSources(sources));
    };

    const handleQuestionClick = (question: string, flowKey?: string) => {
        autoFlowKeyRef.current = flowKey;
        handleOpenModal();
        setAutoSendMessage(question);
    };

    const handleSuggestionClick = (suggestion: SuggestedQuestion) => {
        const { text, flowKey } = suggestion;
        if (!isExpanded) {
            autoFlowKeyRef.current = flowKey;
            handleOpenModal();
            setAutoSendMessage(text);
        } else {
            if (sendMessageRef.current) {
                sendMessageRef.current(text, undefined, flowKey);
            } else {
                sendMessage(text, flowKey);
            }
        }
    };

    // Handle backdrop click to close modal
    const handleBackdropClick = (event: React.MouseEvent) => {
        if (isExpanded && event.target === event.currentTarget) {
            handleCloseModal();
        }
    };

    if (!showAskAica) return <></>;

    return (
        <>
            {/* Backdrop - only show when expanded and hide during closing when opened from global */}
            {isExpanded && !(openedFromGlobal && isClosing) && (
                <div className={`${styles.Backdrop} ${isAnimating ? styles.BackdropAnimating : ''}`} onClick={handleBackdropClick} />
            )}

            {/* Expandable Ask Bar */}
            {(!global || showModalContent || (openedFromGlobal && isAnimating)) && (
                <div
                    className={`${styles.FloatingAskBar} ${isExpanded ? styles.Expanded : ''} ${isAnimating ? styles.Animating : ''}`}
                    ref={barRef}
                >
                    {/* Collapsed Content - hidden during closing animation when opened from global */}
                    {showCollapsedContent && !(openedFromGlobal && isClosing) && (
                        <div className={styles.CollapsedContent}>
                            {/* Row 1: Ask anything text */}
                            <div className={styles.Row1} onClick={handleOpenModal}>
                                <span className={styles.AskAnythingText}>Ask anything</span>
                            </div>

                            {/* Row 2: Data source selector + Suggested questions */}
                            <div className={styles.Row2}>
                                {/* Data Source Selector */}
                                <div className={styles.DataSourceSection}>
                                    <DataSourceSelector
                                        selectedSources={selectedSources}
                                        onSelectionChange={handleDataSourceChange}
                                        onClose={() => {}}
                                        buttonStyle="collapsed"
                                    />
                                </div>

                                {/* Suggested Questions - 70% */}
                                <div className={styles.SuggestedQuestionsSection} onClick={handleOpenModal}>
                                    {suggestedQuestions.map((question) => (
                                        <div
                                            key={question.id}
                                            className={styles.QuestionPill}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleQuestionClick(question.text, question.flowKey);
                                            }}
                                        >
                                            <MagicUploadIcon colour="#4D4D4D" />
                                            {question.display || question.text}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Modal Content - shown when expanded */}
                    {showModalContent && (
                        <div className={`${styles.ModalContent} ${showModalContent ? styles.visible : ''}`}>
                            {/* Left Panel - Chat Interface */}
                            <LeftPanel
                                selectedCompany={selectedCompany}
                                overviewData={overviewData}
                                onSendMessageRef={sendMessageRef}
                                onClearChat={clearChatMemory}
                                onOpenFile={openFile}
                                onAddFileToReports={addToReports}
                            />

                            {/* Right Panel - Suggestions/Reasoning */}
                            <RightPanel
                                mode={rightPanelMode}
                                onModeChange={(mode) => dispatch(setAskAicaRightPanelMode(mode))}
                                onCrossButtonClick={handleCloseModal}
                                onSuggestionClick={handleSuggestionClick}
                                onOpenFile={openFile}
                                onAddFileToReports={addToReports}
                            />
                        </div>
                    )}
                </div>
            )}

            {global && showGlobalIcon && (
                <div className={styles.GlobalIcon} ref={globalIconRef} onClick={handleOpenModal}>
                    <img src={chatbotGif} height={'60px'} />
                </div>
            )}

            {/* File viewer drawers — mounted once, driven by useAskAicaFileActions.
                Docx/Sheet feed on the pre-fetched Athena blob; never the raw fileUrl. */}
            {docxViewer && (
                <DocxDrawer
                    open
                    onClose={docxViewer.onClose}
                    fileName={docxViewer.fileName}
                    blob={docxViewer.blob}
                    onDownload={docxViewer.onDownload}
                    onAddToReports={docxViewer.onAddToReports}
                    addingToReports={addingToReports}
                    addedToReports={addedToReports}
                />
            )}
            {sheetViewer && (
                <SheetEditorDrawer
                    open
                    onClose={sheetViewer.onClose}
                    fileName={sheetViewer.fileName}
                    sheets={sheetViewer.sheets}
                    onAddToReports={sheetViewer.onAddToReports}
                    addingToReports={addingToReports}
                    addedToReports={addedToReports}
                />
            )}
            {fileViewer && (
                <FileViewerDrawer
                    heading={fileViewer.fileName}
                    fileUrl={fileViewer.url}
                    mimeType={fileViewer.mimeType}
                    onDownload={fileViewer.onDownload}
                    onClose={fileViewer.onClose}
                />
            )}
        </>
    );
};

export default AskAicaV2;
